// ============================================================================
//  ClaimVerdictPanel — what NHIA said about this claim
// ============================================================================
//
//  Sits at the top of the claim editor. When a submitted batch's import report
//  has been loaded, the claim that came back rejected says WHY, here, on the
//  screen where it is edited — instead of the officer resubmitting it unchanged
//  and finding out again a week later.
//
//  ── IT SHOWS THE SCHEME'S OWN WORDS ────────────────────────────────────────
//
//  Each row is NHIA's message verbatim, with a plain explanation of the rule
//  underneath and, where there is one, which line it is about ("Medicine 3").
//  The verbatim text matters: an officer who has to raise this with NHIA needs
//  the sentence NHIA used, not our paraphrase of it.
//
//  Thirteen of the eighteen rules cannot be fixed from here, and each says so and
//  why rather than offering a button that would do nothing. Several depend on
//  facility credentialling and prescribing level, which this system does not
//  hold — and two locally-derived approximations of those rules were measured
//  against a real report and were wrong by 33x and by 1,090 rows.
//
//  ── AN EMPTY PANEL IS AMBIGUOUS, SO IT IS NOT DRAWN ────────────────────────
//
//  A claim with no verdicts is either one NHIA accepted or one that has never
//  been submitted, and those are very different things. The panel therefore
//  renders NOTHING at all rather than an empty box that would read as approval.
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  AlertTriangle, XCircle, ChevronDown, ChevronRight, Wrench, Info,
} from 'lucide-react';
import { claimStoreVerdict } from '../BaseURL';
import { BRAND } from '../../brand';

const TONE = {
  error: { bg: BRAND.dangerSoft, bd: 'rgba(166,40,40,.28)', fg: '#A62828',
    icon: XCircle, word: 'Rejected' },
  warning: { bg: '#FFF4E5', bd: 'rgba(154,91,0,.28)', fg: '#7A4A00',
    icon: AlertTriangle, word: 'Queried' },
};

export default function ClaimVerdictPanel({ claimId }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);

  const load = useCallback(async () => {
    if (!claimId) { setData(null); return; }
    try {
      const r = await axios.get(claimStoreVerdict, { params: { id: claimId } });
      setData(r.data?.success ? r.data : null);
    } catch {
      //  Silent. This is context on a claim, not the claim itself — a failed
      //  lookup should leave the editor working, not put an error above it.
      setData(null);
    }
  }, [claimId]);

  useEffect(() => { load(); }, [load]);

  const verdicts = data?.verdicts || [];
  //  See the header: nothing to say means nothing drawn.
  if (!verdicts.length) return null;

  const errors = verdicts.filter((v) => v.severity === 'error');
  const warnings = verdicts.filter((v) => v.severity !== 'error');
  const worst = errors.length ? 'error' : 'warning';
  const t = TONE[worst];
  const Icon = t.icon;
  const report = verdicts[0]?.report || {};

  return (
    <div style={{
      border: `1px solid ${t.bd}`, borderRadius: 10, background: t.bg,
      marginBottom: 12, overflow: 'hidden',
    }}>
      <button type="button" onClick={() => setOpen((o) => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10, width: '100%',
          padding: '10px 12px', border: 0, background: 'none', font: 'inherit',
          cursor: 'pointer', textAlign: 'left', color: t.fg,
        }}>
        <Icon size={16} style={{ flexShrink: 0 }} />
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 800 }}>
            NHIA {t.word.toLowerCase()} this claim
            {errors.length ? ` — ${errors.length} error${errors.length === 1 ? '' : 's'}` : ''}
            {warnings.length ? `${errors.length ? ',' : ' —'} `
              + `${warnings.length} warning${warnings.length === 1 ? '' : 's'}` : ''}
          </span>
          <span style={{ display: 'block', fontSize: 11, opacity: .85, marginTop: 1 }}>
            {report.title || 'from an imported report'}
            {' · '}the scheme&apos;s own words, below
          </span>
        </span>
        {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
      </button>

      {open && (
        <div style={{ padding: '0 12px 11px', display: 'grid', gap: 7 }}>
          {[...errors, ...warnings].map((v) => {
            const vt = TONE[v.severity] || TONE.warning;
            return (
              <div key={v.id} style={{
                background: '#fff', border: `1px solid ${vt.bd}`, borderRadius: 8,
                padding: '9px 11px',
              }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 7,
                  flexWrap: 'wrap' }}>
                  <span style={{
                    display: 'inline-block', padding: '1px 6px', borderRadius: 999,
                    fontSize: 9.5, fontWeight: 800, textTransform: 'uppercase',
                    letterSpacing: .3, background: vt.bg, color: vt.fg,
                    border: `1px solid ${vt.bd}`, whiteSpace: 'nowrap',
                  }}>{v.severity}</span>

                  <span style={{ fontSize: 12.5, fontWeight: 800, color: BRAND.ink }}>
                    {v.title || 'NHIA reported a problem'}
                  </span>

                  {/*  Which line, when the message names one. An officer with
                       fourteen medicines needs to know it is the third. */}
                  {v.subject?.kind && (
                    <span style={{ fontSize: 11, color: BRAND.inkMuted }}>
                      {v.subject.kind} {v.subject.index}
                      {v.subject.code ? ` · ${v.subject.code}` : ''}
                    </span>
                  )}

                  <span style={{ flex: 1 }} />

                  {v.fixable ? (
                    <span title={v.fixNote || ''} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      fontSize: 10.5, fontWeight: 800, color: '#1E6B39',
                      background: '#E8F5EC', border: '1px solid rgba(30,107,57,.22)',
                      padding: '1px 7px', borderRadius: 999, whiteSpace: 'nowrap',
                    }}><Wrench size={10} /> can be fixed in bulk</span>
                  ) : (
                    <span title={v.fixNote || ''} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      fontSize: 10.5, fontWeight: 700, color: BRAND.inkMuted,
                      whiteSpace: 'nowrap',
                    }}><Info size={10} /> needs a person</span>
                  )}
                </div>

                {/*  The explanation, then NHIA's exact sentence. Both, because the
                     explanation is what an officer acts on and the exact sentence
                     is what they quote if they have to query it. */}
                {v.why && (
                  <div style={{ marginTop: 5, fontSize: 11.5, color: BRAND.inkSoft,
                    lineHeight: 1.55 }}>{v.why}</div>
                )}
                <div style={{
                  marginTop: 6, fontSize: 11, color: BRAND.inkMuted, lineHeight: 1.5,
                  paddingLeft: 9, borderLeft: `2px solid ${BRAND.line}`,
                  fontFamily: 'inherit',
                }}>{v.message}</div>

                {/*  Why nothing can be done from here. Shown rather than hidden in
                     a tooltip for the ones that are not fixable — it is the
                     difference between "nobody has built this" and "this cannot
                     be answered from our data". */}
                {!v.fixable && v.fixNote && (
                  <div style={{ marginTop: 5, fontSize: 10.5, color: BRAND.inkFaint,
                    lineHeight: 1.5 }}>{v.fixNote}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
